import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { setCurrentUser } from './currentUserSlice';
import {
  addCompanyInvestor,
  setCompanyInvestors,
} from './companyInvestorsSlice';
import type {
  DecoratedInvestor,
  UndecoratedInvestor,
} from '@flemingmetrics/api-client';
import { addToIdArray, filterByValue, mapToKey } from '@/utils/arrays';
import type { ByIdDictionary } from '@/types';

type InvestorsState = {
  byId: Record<string, UndecoratedInvestor>;
  byProfileId?: ByIdDictionary;
};

const initialState: InvestorsState = { byId: {} };

export const investorsSlice = createSlice({
  name: 'investors',
  initialState,
  reducers: {
    addInvestor: (state, action: PayloadAction<DecoratedInvestor>) => {
      return getNextState(state, [action.payload]);
    },
    setInvestors: (state, action: PayloadAction<DecoratedInvestor[]>) => {
      return getNextState(state, action.payload);
    },
    deleteInvestor: (state, action: PayloadAction<{ investorId: number }>) => {
      const key = `${action.payload.investorId}`;
      if (!state.byId[key]) return state;
      const { profileId } = state.byId[key];
      if (state.byProfileId[profileId]) {
        state.byProfileId[profileId] = filterByValue(
          state.byProfileId[profileId],
          key
        );
      }
      delete state.byId[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCurrentUser, (state, action) => {
        if (action.payload.investor) {
          return getNextState(state, [
            { ...action.payload.investor, profile: action.payload.profile },
          ]);
        }
        return state;
      })
      .addCase(setCompanyInvestors, (state, action) => {
        return getNextState(state, mapToKey(action.payload, 'investor'));
      })
      .addCase(addCompanyInvestor, (state, action) => {
        return getNextState(state, [action.payload.investor]);
      });
  },
});

function formatInvestor({ profile, ...investor }: DecoratedInvestor) {
  return investor;
}

function getNextState(state: InvestorsState, investors: DecoratedInvestor[]) {
  const byId = { ...state.byId };
  const byProfileId = { ...state.byProfileId };
  investors.forEach((investor) => {
    const key = `${investor.id}`;
    byId[key] = formatInvestor(investor);
    byProfileId[key] = addToIdArray(byProfileId[investor.profileId], key);
  });
  return { byId };
}

export const { addInvestor, setInvestors, deleteInvestor } =
  investorsSlice.actions;

export default investorsSlice.reducer;
